import { uiPaper } from "shared/ui/uiSurface"
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material"
import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateClientMutation,
  useGetClientByIdQuery,
  useUpdateClientMutation,
} from "../../apis/appApi"

type CustomerFormDialogProps = {
  open: boolean
  onClose: () => void
  onSuccess: (message: string) => void
  clientId?: number | null
}

export const CustomerFormDialog: FC<CustomerFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  clientId,
}) => {
  const { t } = useTranslation()
  const isEdit = clientId != null

  const { data: response } = useGetClientByIdQuery(clientId!, {
    skip: !isEdit,
  })
  const [createClient, { isLoading: isCreating }] = useCreateClientMutation()
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation()

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [organisation, setOrganisation] = useState("")
  const [language, setLanguage] = useState("")
  const [notes, setNotes] = useState("")
  const [externalReference, setExternalReference] = useState("")
  const [includesInSurvey, setIncludesInSurvey] = useState(false)
  const [includesInTakeInChargeNotifications, setIncludesInTakeInChargeNotifications] = useState(false)

  // Pre-fill form when editing
  useEffect(() => {
    if (isEdit && response?.data) {
      const c = response.data
      setFirstName(c.firstName || "")
      setLastName(c.lastName || "")
      setEmail(c.email || "")
      setOrganisation(c.organisation || "")
      setLanguage(c.language || "")
      setNotes(c.notes || "")
      setExternalReference(c.externalReference || "")
      setIncludesInSurvey(c.includesInSurvey ?? false)
      setIncludesInTakeInChargeNotifications(c.includesInTakeInChargeNotifications ?? false)
    } else if (!isEdit && open) {
      setFirstName("")
      setLastName("")
      setEmail("")
      setOrganisation("")
      setLanguage("")
      setNotes("")
      setExternalReference("")
      setIncludesInSurvey(false)
      setIncludesInTakeInChargeNotifications(false)
    }
  }, [isEdit, response, open])

  const handleClose = () => {
    onClose()
  }

  const handleSubmit = async () => {
    try {
      const body = {
        firstName,
        lastName,
        email: email || null,
        organisation: organisation || null,
        language: language || null,
        notes: notes || null,
        externalReference: externalReference || null,
        includesInSurvey,
        includesInTakeInChargeNotifications,
      }
      if (isEdit) {
        await updateClient({ id: clientId!, body }).unwrap()
        onSuccess(t("clients.editSuccess"))
      } else {
        await createClient(body).unwrap()
        onSuccess(t("clients.createSuccess"))
      }
      handleClose()
    } catch {
      onSuccess(t("clients.saveError"))
    }
  }

  const isValid = firstName.trim() !== "" && lastName.trim() !== ""
  const isSaving = isCreating || isUpdating

  return (
    <Dialog slotProps={{ paper: uiPaper("UI-DLV2-095") }} open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="customer-form-dialog">
      <DialogTitle>
        {isEdit ? t("clients.editTitle") : t("clients.createTitle")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t("clients.firstName")}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              fullWidth
              size="small"
              autoFocus
              data-testid="customer-firstName-input"
            />
            <TextField
              label={t("clients.lastName")}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
              size="small"
              data-testid="customer-lastName-input"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t("clients.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              type="email"
              data-testid="customer-email-input"
            />
            <TextField
              label={t("clients.organisation")}
              value={organisation}
              onChange={(e) => setOrganisation(e.target.value)}
              fullWidth
              size="small"
              data-testid="customer-organisation-input"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label={t("clients.language")}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              fullWidth
              size="small"
              data-testid="customer-language-select"
            >
              <MenuItem value="">{"\u2014"}</MenuItem>
              <MenuItem value="fr">{t("clients.languageValue.fr")}</MenuItem>
              <MenuItem value="en">{t("clients.languageValue.en")}</MenuItem>
            </TextField>
            <TextField
              label={t("clients.externalReference")}
              value={externalReference}
              onChange={(e) => setExternalReference(e.target.value)}
              fullWidth
              size="small"
              data-testid="customer-externalReference-input"
            />
          </Stack>
          <TextField
            label={t("clients.notes")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={3}
            data-testid="customer-notes-input"
          />
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includesInSurvey}
                  onChange={(e) => setIncludesInSurvey(e.target.checked)}
                  data-testid="customer-includesInSurvey-checkbox"
                />
              }
              label={t("clients.includesInSurvey")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includesInTakeInChargeNotifications}
                  onChange={(e) => setIncludesInTakeInChargeNotifications(e.target.checked)}
                  data-testid="customer-includesInTakeInCharge-checkbox"
                />
              }
              label={t("clients.includesInTakeInChargeNotifications")}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-testid="customer-form-cancel-btn">{t("cancel")}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || isSaving}
          data-testid="customer-form-submit-btn"
        >
          {isEdit ? t("save") : t("clients.create")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
